import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

export type Connections = {
  /** Every call between two components, from caller to callee, where at least one side is owned. */
  edges: [from: string, to: string][];
  /** Components owned by someone else that call one of ours. */
  callers: string[];
  /** Components owned by someone else that one of ours calls. */
  callees: string[];
};

const isComponent = (ref: string) => ref.startsWith('component:');

/** How a team's services connect to each other and to other teams, from their dependsOn / dependencyOf relations. */
export const buildConnections = (owned: Entity[]): Connections => {
  const own = new Set(owned.map(stringifyEntityRef));
  const edges = new Map<string, [string, string]>();
  for (const entity of owned) {
    const self = stringifyEntityRef(entity);
    for (const { type, targetRef } of entity.relations ?? []) {
      if (!isComponent(targetRef)) continue;
      if (type === 'dependsOn')
        edges.set(`${self}>${targetRef}`, [self, targetRef]);
      if (type === 'dependencyOf')
        edges.set(`${targetRef}>${self}`, [targetRef, self]);
    }
  }
  const all = [...edges.values()];
  const uniq = (refs: string[]) => [...new Set(refs)];
  return {
    edges: all,
    callers: uniq(
      all
        .filter(([from, to]) => own.has(to) && !own.has(from))
        .map(([from]) => from),
    ),
    callees: uniq(
      all
        .filter(([from, to]) => own.has(from) && !own.has(to))
        .map(([, to]) => to),
    ),
  };
};
